import React, { useEffect, useRef } from 'react';
import cardSurface from '../assets/tutti-member-card-surface.png';
import '../styles/tutti-membership-card.css';

const cardCopy = {
  en: {
    label: 'Your PeaceTech membership card', name: 'PeaceTech member',
    monthly: 'Monthly membership', yearly: 'Annual membership', plan: 'Membership',
    trialEnd: 'Trial until', periodEnd: 'Valid until', accessEnd: 'Access until',
    expiredEnd: 'Ended on', paymentEnd: 'Current period ends', unknownEnd: 'Date unavailable',
    test: 'Stripe TEST mode', renewalOff: 'Renewal off',
    statuses: { none: 'No subscription', trialing: 'Trial active', active: 'Active', past_due: 'Payment due', expired: 'Expired', canceled: 'Canceled' },
  },
  zh: {
    label: '你的 PeaceTech 会员卡', name: 'PeaceTech 会员',
    monthly: '月付会员', yearly: '年付会员', plan: '会员套餐',
    trialEnd: '试用至', periodEnd: '有效至', accessEnd: '使用至',
    expiredEnd: '结束于', paymentEnd: '当前周期至', unknownEnd: '日期暂不可用',
    test: 'Stripe TEST 测试模式', renewalOff: '已关闭续费',
    statuses: { none: '尚未订阅', trialing: '试用中', active: '订阅有效', past_due: '付款待处理', expired: '已到期', canceled: '已取消' },
  },
};

function membershipDetails(entitlement, language) {
  const copy = cardCopy[language === 'zh' ? 'zh' : 'en'];
  const status = Object.hasOwn(copy.statuses, entitlement?.status) ? entitlement.status : 'none';
  const trial = status === 'trialing';
  const ended = status === 'expired' || (status === 'canceled' && !entitlement?.hasAccess);
  const value = trial ? entitlement?.trialEndsAt : entitlement?.currentPeriodEndsAt;
  const date = value ? new Date(value) : null;
  const validDate = date && Number.isFinite(date.getTime());
  return {
    copy,
    status,
    statusLabel: copy.statuses[status],
    planLabel: ['monthly', 'yearly'].includes(entitlement?.plan) ? copy[entitlement.plan] : copy.plan,
    dateLabel: trial ? copy.trialEnd : ended ? copy.expiredEnd : status === 'past_due' ? copy.paymentEnd : entitlement?.cancelAtPeriodEnd ? copy.accessEnd : copy.periodEnd,
    dateTime: validDate ? date.toISOString() : null,
    dateText: validDate ? new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: language === 'zh' ? '2-digit' : 'short', day: language === 'zh' ? '2-digit' : 'numeric' }).format(date) : copy.unknownEnd,
  };
}

// Decoration is local to the card; no account data is included in its image asset.
export default function TuttiMembershipCard({ user, entitlement, language = 'en', testMode = false }) {
  const card = useRef(null);
  const motionAllowed = useRef(false);
  const frame = useRef(null);
  const details = membershipDetails(entitlement, language);
  const { copy } = details;
  const memberName = user?.displayName?.trim() || user?.email || copy.name;

  function resetCard() {
    if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    frame.current = null;
    if (!card.current) return;
    card.current.style.setProperty('--member-rotate-x', '0deg');
    card.current.style.setProperty('--member-rotate-y', '0deg');
    card.current.style.setProperty('--member-light-x', '72%');
    card.current.style.setProperty('--member-light-y', '20%');
    card.current.removeAttribute('data-tilting');
  }

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
    const updateMotion = () => {
      motionAllowed.current = media.matches;
      if (!media.matches) resetCard();
    };
    updateMotion();
    media.addEventListener?.('change', updateMotion);
    return () => {
      media.removeEventListener?.('change', updateMotion);
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    };
  }, []);

  function moveCard(event) {
    if (!motionAllowed.current || event.pointerType === 'touch' || !card.current) return;
    // The untransformed stage is the reference, so rotation cannot chase the pointer.
    const bounds = event.currentTarget.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const y = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height));
    if (frame.current !== null) window.cancelAnimationFrame(frame.current);
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      if (!card.current || !motionAllowed.current) return;
      card.current.style.setProperty('--member-rotate-x', `${((0.5 - y) * 7).toFixed(2)}deg`);
      card.current.style.setProperty('--member-rotate-y', `${((x - 0.5) * 9).toFixed(2)}deg`);
      card.current.style.setProperty('--member-light-x', `${(x * 100).toFixed(1)}%`);
      card.current.style.setProperty('--member-light-y', `${(y * 100).toFixed(1)}%`);
      card.current.setAttribute('data-tilting', 'true');
    });
  }

  return <div className="th-member-card-stage" data-testid="membership-card-stage" onPointerMove={moveCard} onPointerLeave={resetCard} onPointerCancel={resetCard}>
    <figure ref={card} className="th-member-card" aria-label={copy.label} data-status={details.status}>
      <img className="th-member-card-surface" src={cardSurface} alt="" aria-hidden="true" draggable="false" />
      <div className="th-member-card-shade" aria-hidden="true" />
      <div className="th-member-card-gloss" aria-hidden="true" />
      <div className="th-member-card-content">
        <div className="th-member-card-top">
          <div className="th-member-card-brand"><span className="th-member-card-mark" aria-hidden="true"><i /><i /><i /><i /></span><span>PEACETECH</span></div>
          <div className="th-member-card-badges">{testMode && <span className="th-member-card-test" aria-label={copy.test}>TEST</span>}<span className="th-member-card-status">{details.statusLabel}</span></div>
        </div>
        <div className="th-member-card-heading"><span className="th-member-card-kicker">TUTTI MEMBERSHIP</span><span className="th-member-card-title">Member</span></div>
        <div className="th-member-card-bottom">
          <div className="th-member-card-owner"><span className="th-member-card-name" title={memberName}>{memberName}</span><span className="th-member-card-plan">{details.planLabel}</span></div>
          <div className="th-member-card-validity">{entitlement?.cancelAtPeriodEnd && <span className="th-member-card-renewal">{copy.renewalOff}</span>}<span className="th-member-card-date-label">{details.dateLabel}</span>{details.dateTime ? <time dateTime={details.dateTime}>{details.dateText}</time> : <span>{details.dateText}</span>}</div>
        </div>
      </div>
    </figure>
  </div>;
}
